<script setup lang="ts">
/**
 * Code block with copy-to-clipboard button (T07).
 *
 * Optional `language` prop enables highlight.js syntax highlighting (python,
 * typescript, bash, json registered in `lib/highlight.ts`). Omit it for plain
 * single-line snippets (e.g. the Authorization header). The Copy button always
 * copies the raw `code`, never the highlighted markup.
 *
 * Copy strategy:
 *   - Uses `navigator.clipboard.writeText` when available (HTTPS/secure context).
 *   - Falls back to a transient `<textarea> + document.execCommand('copy')`
 *     so dev on http://localhost still works.
 *   - "Copied" badge auto-hides after 1.5s.
 */
import { computed, ref } from 'vue';
import { highlightCode } from '../../lib/highlight';

const props = defineProps<{
  /** Source text to display and copy. */
  code: string;
  /** Optional caption rendered above the block (e.g. "Python"). */
  label?: string;
  /** highlight.js language id (python | typescript | bash | json). */
  language?: string;
}>();

const highlighted = computed(() => highlightCode(props.code, props.language));

const copied = ref(false);
let resetTimer: ReturnType<typeof setTimeout> | null = null;

async function copy(text: string): Promise<void> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for http://localhost dev.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(ta);
      }
    }
    copied.value = true;
    if (resetTimer) clearTimeout(resetTimer);
    resetTimer = setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch {
    // Swallow — best-effort UX, no user-facing error needed.
  }
}
</script>

<template>
  <div class="relative group">
    <div v-if="label" class="text-sm font-medium text-ink mb-2">{{ label }}</div>
    <div class="relative">
      <pre
        class="bg-surface-soft border border-hairline rounded-lg px-4 py-3 overflow-x-auto text-xs font-mono text-ink whitespace-pre"
      ><code class="hljs" v-html="highlighted"></code></pre>
      <button
        type="button"
        class="absolute top-2 right-2 text-[11px] px-2.5 py-1 rounded-full bg-canvas border border-hairline text-body hover:text-ink hover:border-hairline-strong transition"
        :aria-label="copied ? 'Copied to clipboard' : 'Copy to clipboard'"
        @click="copy(code)"
      >
        {{ copied ? 'Copied' : 'Copy' }}
      </button>
    </div>
  </div>
</template>
